"use strict";
/**
 * InkStep shared types — sequence, word bank, scheme discriminator.
 *
 * WP-065: DB-backed InkStep phonics sequence and word bank.
 * This is the foundation for the InkStep (licence-free RWI alternative) track.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GraphemeDecompositionError = exports.FRESH_START_PACING = exports.STANDARD_PACING = exports.TRACK_VALUES = exports.SCHEME_VALUES = void 0;
exports.getPacingForTrack = getPacingForTrack;
exports.decomposeWordIntoGraphemes = decomposeWordIntoGraphemes;
/**
 * The scheme discriminator values. Used on `activities.scheme` and
 * `learning_kits.scheme` columns to identify InkStep content.
 */
exports.SCHEME_VALUES = ['inkstep', 'other'];
/**
 * WP-076: Track discriminator — 'standard' (default pacing) or 'fresh_start'
 * (accelerated KS2 catch-up pacing). Internal code name only; user-facing
 * label is "KS2 Catch-Up" (per IP/trademark note in WP-076 spec).
 */
exports.TRACK_VALUES = ['standard', 'fresh_start'];
/**
 * WP-076: Pacing constants (positions per week).
 * Standard track: 1 position/week (default for Reception–KS1).
 * Fresh Start track: 2 positions/week (accelerated for KS2 catch-up).
 */
exports.STANDARD_PACING = 1;
exports.FRESH_START_PACING = 2;
/**
 * Returns the pacing (positions per week) for a given track.
 * @param track The InkStep track ('standard' or 'fresh_start').
 * @returns Positions per week (1 for standard, 2 for fresh_start).
 */
function getPacingForTrack(track) {
    return track === 'fresh_start' ? exports.FRESH_START_PACING : exports.STANDARD_PACING;
}
/**
 * Error thrown when grapheme decomposition fails — a letter or combination
 * in the word is not in the known grapheme set. This is a typed error, never
 * a silent failure (per rules/02 error handling).
 */
class GraphemeDecompositionError extends Error {
    position;
    letter;
    constructor(position, letter) {
        super(`Cannot decompose word at position ${position}: letter "${letter}" is not in the grapheme set`);
        this.name = 'GraphemeDecompositionError';
        this.position = position;
        this.letter = letter;
    }
}
exports.GraphemeDecompositionError = GraphemeDecompositionError;
/**
 * Decompose a word into its constituent graphemes using a longest-match
 * left-to-right algorithm against the sequence's known grapheme set.
 *
 * The algorithm:
 * 1. Sort the grapheme set by length descending (longest first).
 * 2. Scan the word left-to-right.
 * 3. At each position, try the longest matching grapheme first, fall back to shorter.
 * 4. If no grapheme matches at a position, throw GraphemeDecompositionError.
 *
 * This is deterministic, testable, and requires no external dependency — the
 * correct approach for a v1 phonics scheme where the grapheme set is closed
 * and known.
 *
 * @param word The word to decompose (must be non-empty, lowercase).
 * @param graphemeSet The set of valid graphemes (must be non-empty).
 * @returns An array of graphemes that compose the word.
 * @throws {GraphemeDecompositionError} If a letter is not in the grapheme set.
 */
function decomposeWordIntoGraphemes(word, graphemeSet) {
    if (!word || word.length === 0) {
        throw new GraphemeDecompositionError(0, '');
    }
    if (!graphemeSet || graphemeSet.length === 0) {
        throw new GraphemeDecompositionError(0, word[0]);
    }
    // Sort graphemes by length descending (longest first for longest-match)
    const sortedGraphemes = [...graphemeSet].sort((a, b) => b.length - a.length);
    const result = [];
    let i = 0;
    while (i < word.length) {
        let matched = false;
        for (const grapheme of sortedGraphemes) {
            if (word.substring(i, i + grapheme.length) === grapheme) {
                result.push(grapheme);
                i += grapheme.length;
                matched = true;
                break;
            }
        }
        if (!matched) {
            throw new GraphemeDecompositionError(i, word[i]);
        }
    }
    return result;
}
